import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Box,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  useToast,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useCustomContentList } from '../../media-library/hooks/useCustomContent';
import { enqueue } from '../api/queueApi';

type EnqueueAction = 'playNow' | 'playNext' | 'addToQueue';

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  onEnqueued: () => void;
}

export function AnnouncementModal({ isOpen, onClose, locationId, onEnqueued }: AnnouncementModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (isOpen) {
      setSearchInput('');
      setSearch('');
    }
  }, [isOpen]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useCustomContentList({
    q: search || undefined,
  });
  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const handleEnqueue = async (contentId: string, action: EnqueueAction) => {
    setBusyId(contentId);
    try {
      await enqueue(locationId, { contentId, action });
      toast({ status: 'success', title: t('player.announcement.enqueued'), duration: 2000 });
      onEnqueued();
      onClose();
    } catch (e) {
      toast({
        status: 'error',
        title: e instanceof Error ? e.message : t('common.error', 'Something went wrong'),
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>{t('player.announcement.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={t('player.announcement.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="brand.500" />
            </Flex>
          ) : items.length === 0 ? (
            <Text color="gray.400" py={8} textAlign="center">
              {t('player.announcement.empty')}
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {items.map((clip) => {
                const ready = clip.processingStatus === 'completed';
                return (
                  <HStack key={clip.id} py={2} px={1} spacing={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="500" noOfLines={1}>
                        {clip.title}
                      </Text>
                      {!ready && (
                        <Text fontSize="xs" color="gray.400">
                          {t('player.announcement.processing')}
                        </Text>
                      )}
                    </Box>
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        rightIcon={<ChevronDownIcon />}
                        isDisabled={!ready}
                        isLoading={busyId === clip.id}
                      >
                        {t('player.announcement.add')}
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => handleEnqueue(clip.id, 'playNow')}>
                          {t('player.announcement.playNow')}
                        </MenuItem>
                        <MenuItem onClick={() => handleEnqueue(clip.id, 'playNext')}>
                          {t('player.announcement.playNext')}
                        </MenuItem>
                        <MenuItem onClick={() => handleEnqueue(clip.id, 'addToQueue')}>
                          {t('player.announcement.addToQueue')}
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                );
              })}
              {hasNextPage && (
                <Flex justify="center" mt={3}>
                  <Button size="sm" variant="ghost" onClick={() => fetchNextPage()} isLoading={isFetchingNextPage}>
                    {t('playlists.loadMore')}
                  </Button>
                </Flex>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AnnouncementModal;
